/**
 * Process (Contour) detail — the shared Inspector grammar (design-system §5)
 * over a `/api/processes` facade record (one row in the unified `packages`
 * store, `package_type='contour'`; the process `id` is the package id).
 *
 * Header carries the named **open graph** primary action (→
 * `/processes/:id/graph`, `11 §3.5`) plus **compile** (`[PROPOSED]`); Overview
 * is a FieldGrid; Relations lists the owned Phases (RefLinks) and the contour's
 * outgoing/incoming `package_links`; Audit hits the generic timeline (teaser
 * until it ships). Phases keep their existing list so the e2e Process-Graph
 * flow stays intact.
 */
import * as React from "react";
import {
  useShow,
  useList,
  useGo,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import {
  Boxes,
  Flag,
  FlagOff,
  GitBranch,
  Layers,
  Pencil,
  Trash2,
} from "@/components/ui/icon";

import type { Phase, Process, PackageLink } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  type RelationGroup,
  type InspectorAction,
} from "@/components/inspector";
import { formatDateTime } from "@/lib/format";
import { humanize } from "@/resources/projects/_form";
import { useApiAction } from "@/resources/_shared/useApiAction";
import { buildPackageRelations } from "@/resources/packages/_actions";

export function ProcessShow() {
  const go = useGo();
  const { mutate: deleteProcess } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState(false);
  const { query } = useShow<Process, HttpError>({ resource: "processes" });
  const process = query.data?.data;
  const refetch = () => {
    void query.refetch();
  };

  const { data: phasesData } = useList<Phase, HttpError>({
    resource: "phases",
    pagination: { pageSize: 200, mode: "server" },
    filters: process
      ? [{ field: "process_id", operator: "eq", value: process.id }]
      : [],
    sorters: [{ field: "sort_order", order: "asc" }],
    queryOptions: { enabled: Boolean(process) },
  });

  const { data: outgoing } = useList<PackageLink, HttpError>({
    resource: "package-links",
    filters: process
      ? [{ field: "source_package_id", operator: "eq", value: process.id }]
      : [],
    pagination: { pageSize: 200, mode: "server" },
    queryOptions: { enabled: Boolean(process) },
  });
  const { data: incoming } = useList<PackageLink, HttpError>({
    resource: "package-links",
    filters: process
      ? [{ field: "target_package_id", operator: "eq", value: process.id }]
      : [],
    pagination: { pageSize: 200, mode: "server" },
    queryOptions: { enabled: Boolean(process) },
  });

  if (query.isLoading) return <ShowPageSkeleton />;
  if (query.isError || !process) {
    return (
      <ShowPageError
        message={query.error?.message ?? "Process not found."}
        onRetry={refetch}
      />
    );
  }

  const phases = phasesData?.data ?? [];
  const phaseGroup: RelationGroup = {
    title: "Phases",
    emptyMessage: "No phases defined.",
    items: phases.map((phase) => ({
      key: phase.id,
      label: `${phase.sort_order ?? "—"} · ${
        phase.is_start ? "start" : phase.is_terminal ? "terminal" : "phase"
      }`,
      icon: phase.is_start ? Flag : phase.is_terminal ? FlagOff : Layers,
      resource: "phases",
      id: phase.id,
      labelField: "name",
    })),
  };
  const linkGroups = buildPackageRelations(
    { id: process.id } as never,
    outgoing?.data ?? [],
    incoming?.data ?? [],
  );

  const menuActions: InspectorAction[] = [
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => go({ to: `/processes/${process.id}/edit` }),
    },
    {
      label: "Delete",
      icon: Trash2,
      destructive: true,
      onClick: () => setPendingDelete(true),
    },
  ];

  return (
    <>
      <Inspector>
        <InspectorHeader
          title={process.name}
          subtitle="A process package — its phases, owner, and typed links."
          status={process.status}
          actions={<CompileContour packageId={process.id} />}
          primaryAction={
            <Button
              size="sm"
              data-testid="process-open-graph"
              onClick={() => go({ to: `/processes/${process.id}/graph` })}
            >
              <GitBranch className="mr-2 h-4 w-4" />
              Open graph
            </Button>
          }
          menuActions={menuActions}
        />

        <InspectorSection title="Overview">
          <FieldGrid>
            <Field label="Package type">
              <Badge variant="outline">Contour</Badge>
            </Field>
            <Field label="Domain">{process.domain}</Field>
            <Field label="Artifact type">
              {process.artifact_type ? humanize(process.artifact_type) : null}
            </Field>
            <Field label="Rigidity level">{process.rigidity_level}</Field>
            <Field label="Owner">
              {process.owner_user_id ? (
                <RefLink resource="users" id={process.owner_user_id} />
              ) : null}
            </Field>
            <Field label="Created">{formatDateTime(process.created_at)}</Field>
            <Field label="Updated">{formatDateTime(process.updated_at)}</Field>
            {process.purpose ? (
              <Field label="Purpose" full>
                {process.purpose}
              </Field>
            ) : null}
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Relations">
          <RelationsBlock groups={[phaseGroup, ...linkGroups]} />
        </InspectorSection>

        <InspectorSection title="Technical">
          <FieldGrid className="text-muted-foreground">
            <Field label="Id">
              <TechnicalId id={process.id} />
            </Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Audit">
          <AuditBlock entityType="processes" entityId={process.id} />
        </InspectorSection>
      </Inspector>

      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={setPendingDelete}
        title="Delete process?"
        description={
          <>
            This removes <span className="font-medium">{process.name}</span> and
            its contour definition. This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() =>
          new Promise<void>((resolve) => {
            deleteProcess(
              { resource: "processes", id: process.id },
              {
                onSuccess: () => {
                  go({ to: "/processes" });
                  resolve();
                },
                onError: () => resolve(),
              },
            );
          })
        }
      />
    </>
  );
}

/** `compile` action for a contour (`[PROPOSED]` `POST /api/packages/:id/compile`). */
function CompileContour({ packageId }: { packageId: string }) {
  const { run, loading } = useApiAction();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={loading}
      data-testid="process-compile"
      onClick={() =>
        void run(`/packages/${encodeURIComponent(packageId)}/compile`, {
          method: "POST",
          invalidateResources: ["processes", "packages"],
          successMessage: "Compilation requested.",
        }).catch(() => {
          /* [PROPOSED]: error surfaced as a toast, page stays alive. */
        })
      }
    >
      <Boxes className="mr-2 h-4 w-4" />
      {loading ? "Compiling…" : "Compile"}
    </Button>
  );
}

export default ProcessShow;
